/**
 * Where the chairs go around a table.
 *
 * Ported from `menu_waiter_mobile_app_FE/src/features/floor-plan/seatLayout.ts`,
 * which took it from `menu_waiter_FE` before that. Third copy of the same
 * arithmetic and a deliberate one: the three apps share no code, and a room that
 * seats a four-top differently in the waiter's hand and on the venue's website
 * is two drawings of two different restaurants.
 *
 * The only change is the unit. The waiter app measures in rem because it lays
 * the plan out in React Native; this draws into the planner's own pixel space,
 * so the two constants below are the same distances expressed there.
 */

export type TableShape = 'round' | 'square' | 'rectangle'

export interface SeatPos {
  /** Offset from the table's centre, in the planner's units. */
  x: number
  y: number
}

/** 0.45rem and 0.4rem in the waiter app, at its 16px rem. */
export const SEAT_RADIUS = 7.2
const SEAT_GAP = 6.4

/** How far a ring of chairs reaches past the table edge, on one side. */
export const SEAT_REACH = SEAT_GAP + SEAT_RADIUS * 2

export function getSeatPositions(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  if (seatCount <= 0) return []
  if (shape === 'round') return roundSeats(seatCount, width, height)
  return rectSeats(seatCount, width, height)
}

function roundSeats(n: number, width: number, height: number): SeatPos[] {
  const radius = Math.max(width, height) / 2 + SEAT_GAP + SEAT_RADIUS
  const positions: SeatPos[] = []
  for (let i = 0; i < n; i += 1) {
    // Less a quarter turn, so the first chair is at the top rather than at
    // three o'clock. A ring that starts on the right reads as rotated.
    const angle = (2 * Math.PI * i) / n - Math.PI / 2
    positions.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) })
  }
  return positions
}

function rectSeats(n: number, width: number, height: number): SeatPos[] {
  const halfWidth = width / 2
  const halfHeight = height / 2
  const offset = SEAT_GAP + SEAT_RADIUS
  const [top, bottom, left, right] = distribute(n, [
    width,
    width,
    height,
    height
  ])
  const positions: SeatPos[] = []

  spread(top).forEach(f =>
    positions.push({ x: -halfWidth + f * width, y: -halfHeight - offset })
  )
  spread(bottom).forEach(f =>
    positions.push({ x: -halfWidth + f * width, y: halfHeight + offset })
  )
  spread(left).forEach(f =>
    positions.push({ x: -halfWidth - offset, y: -halfHeight + f * height })
  )
  spread(right).forEach(f =>
    positions.push({ x: halfWidth + offset, y: -halfHeight + f * height })
  )
  return positions
}

/**
 * Shares the chairs between the four sides in proportion to their length, then
 * hands what is left over to the sides that came closest to another whole one.
 * A long table gets its chairs along the long sides, which is where they are.
 */
function distribute(total: number, weights: number[]): number[] {
  const sum = weights.reduce((acc, weight) => acc + weight, 0)
  if (sum === 0) return weights.map(() => 0)

  const raw = weights.map(weight => (total * weight) / sum)
  const floors = raw.map(Math.floor)
  let remainder = total - floors.reduce((acc, count) => acc + count, 0)

  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac)

  for (let i = 0; i < order.length && remainder > 0; i += 1) {
    floors[order[i].index] += 1
    remainder -= 1
  }
  return floors
}

/** Evenly along one side, with a gap at each end rather than a chair on a corner. */
function spread(n: number): number[] {
  const fractions: number[] = []
  for (let k = 0; k < n; k += 1) fractions.push((k + 1) / (n + 1))
  return fractions
}
